import React, { useState, useEffect } from 'react';
import { getBrand } from '../utils/brand';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import LiveSessionsPanel from '../components/LiveSessionsPanel';
import ManagedHomesStrip from '../components/ManagedHomesStrip';
import UpsDashboardStrip from '../components/UpsDashboardStrip';
import IspLinksDashboardStrip from '../components/IspLinksDashboardStrip';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import useIsMobile from '../hooks/useIsMobile';
import { Ticket, CheckCircle2, AlertTriangle, Clock, TrendingUp, Circle, Globe, Clipboard, Instagram, ExternalLink, UserX, ChevronRight, Sparkles, LayoutGrid, Check, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonStatsGrid, SkeletonCard } from '../components/Skeleton';
import HiddenChocolate from '../components/HiddenChocolate';
import AnnouncementModal from '../components/AnnouncementModal';
import WelcomeHelpButton from '../components/WelcomeHelpButton';
import VpsDeployBanner from '../components/VpsDeployBanner';
import DashboardGrid from '../dashboard/DashboardGrid';
import WidgetPicker from '../dashboard/WidgetPicker';
import PresetsMenu from '../dashboard/PresetsMenu';
import { useDashboardLayout } from '../dashboard/useDashboardLayout';
import { track } from '../utils/trackAction';

const PRIORITY_COLORS = { low: '#6b7280', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444' };
const STATUS_COLORS = { new: '#8b5cf6', open: '#3b82f6', pending: '#f59e0b', on_hold: '#a78bfa', resolved: '#10b981', closed: '#6b7280' };

const WMO_WEATHER = {
  0: { emoji: '\u2600\uFE0F', key: 'clearSky' },
  1: { emoji: '\uD83C\uDF24\uFE0F', key: 'partlyCloudy' },
  2: { emoji: '\u26C5', key: 'cloudy' },
  3: { emoji: '\u2601\uFE0F', key: 'overcast' },
  45: { emoji: '\uD83C\uDF2B\uFE0F', key: 'fog' },
  48: { emoji: '\uD83C\uDF2B\uFE0F', key: 'fog' },
  51: { emoji: '\uD83C\uDF26\uFE0F', key: 'drizzle' },
  53: { emoji: '\uD83C\uDF26\uFE0F', key: 'drizzle' },
  55: { emoji: '\uD83C\uDF26\uFE0F', key: 'drizzle' },
  61: { emoji: '\uD83C\uDF27\uFE0F', key: 'rain' },
  63: { emoji: '\uD83C\uDF27\uFE0F', key: 'rain' },
  65: { emoji: '\uD83C\uDF27\uFE0F', key: 'rain' },
  71: { emoji: '\uD83C\uDF28\uFE0F', key: 'snow' },
  73: { emoji: '\uD83C\uDF28\uFE0F', key: 'snow' },
  75: { emoji: '\uD83C\uDF28\uFE0F', key: 'snow' },
  80: { emoji: '\uD83C\uDF26\uFE0F', key: 'showers' },
  81: { emoji: '\uD83C\uDF26\uFE0F', key: 'showers' },
  82: { emoji: '\uD83C\uDF27\uFE0F', key: 'showers' },
  95: { emoji: '\u26C8\uFE0F', key: 'thunderstorm' },
  96: { emoji: '\u26C8\uFE0F', key: 'thunderstorm' },
  99: { emoji: '\u26C8\uFE0F', key: 'thunderstorm' },
};

const WEATHER_LABELS = {
  es: { clearSky: 'Despejado', partlyCloudy: 'Parcialmente nublado', cloudy: 'Nublado', overcast: 'Cubierto', fog: 'Niebla', drizzle: 'Llovizna', rain: 'Lluvia', snow: 'Nieve', showers: 'Chubascos', thunderstorm: 'Tormenta' },
  en: { clearSky: 'Clear sky', partlyCloudy: 'Partly cloudy', cloudy: 'Cloudy', overcast: 'Overcast', fog: 'Fog', drizzle: 'Drizzle', rain: 'Rain', snow: 'Snow', showers: 'Showers', thunderstorm: 'Thunderstorm' },
};

function DailyQuote() {
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/quote-of-the-day');
        if (!cancelled && data?.q) setQuote(data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  if (!quote) return null;

  return (
    <span style={{ fontSize: 12, color: 'var(--c-text-muted)', fontStyle: 'italic', opacity: 0.8 }}>
      "{quote.q}" <span style={{ fontStyle: 'normal', fontWeight: 500, fontSize: 11 }}>— {quote.a}</span>
    </span>
  );
}

function WeatherWidget({ language }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);
  const lang = language === 'en' ? 'en' : 'es';
  const localeStr = language === 'en' ? 'en-US' : 'es-AR';

  useEffect(() => {
    let cancelled = false;
    const fetchWeather = async (lat, lon) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min&hourly=temperature_2m,weather_code&forecast_days=5&timezone=auto`
        );
        const data = await res.json();
        if (!cancelled) setWeather(data);
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Default: Capital Federal, Buenos Aires, Argentina
    fetchWeather(-34.6037, -58.3816);
    return () => { cancelled = true; };
  }, []);

  if (loading || !weather?.current) return null;

  const current = weather.current;
  const daily = weather.daily;
  const hourly = weather.hourly;
  const wmo = WMO_WEATHER[current.weather_code] || WMO_WEATHER[0];
  const labels = WEATHER_LABELS[lang];

  const getHourlyForDay = (dateStr) => {
    if (!hourly?.time) return [];
    const hours = [];
    hourly.time.forEach((t, i) => {
      if (t.startsWith(dateStr)) {
        const hour = parseInt(t.substring(11, 13));
        if (hour % 3 === 0) {
          hours.push({
            hour: t.substring(11, 16),
            temp: Math.round(hourly.temperature_2m[i]),
            wmo: WMO_WEATHER[hourly.weather_code[i]] || WMO_WEATHER[0],
          });
        }
      }
    });
    return hours;
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => { setExpanded(v => !v); setHoveredDay(null); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
          padding: '4px 10px', borderRadius: 8,
          border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
          fontSize: 13, color: 'var(--c-text-muted)', userSelect: 'none'
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{wmo.emoji}</span>
        <span style={{ fontWeight: 600, color: 'var(--c-text)' }}>{Math.round(current.temperature_2m)}°C</span>
        <span style={{ fontSize: 11 }}>{labels[wmo.key]}</span>
      </div>
      {expanded && (
        <div style={{
          // En el celular el botón del clima no está pegado al borde derecho: con
          // right:0 + minWidth:300 el panel se salía por la izquierda y se cortaba
          // (Thiago, 8-oct). En el celular va fijo de borde a borde.
          ...(typeof window !== 'undefined' && window.innerWidth <= 768
            ? { position: 'fixed', left: 16, right: 16, top: '30%' }
            : { position: 'absolute', right: 0, top: 'calc(100% + 8px)', minWidth: 300 }),
          background: 'var(--c-surface)', border: '1px solid var(--c-border)',
          borderRadius: 10, padding: '14px 16px', zIndex: 100, overflowX: 'auto',
          boxShadow: 'var(--shadow-lg)'
        }}>
          <div style={{ fontSize: 11, color: 'var(--c-text-subtle)', marginBottom: 10 }}>
            {lang === 'es' ? 'Humedad' : 'Humidity'}: {current.relative_humidity_2m}% · {lang === 'es' ? 'Viento' : 'Wind'}: {current.wind_speed_10m} km/h
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
            {daily?.time?.map((date, i) => {
              const dayWmo = WMO_WEATHER[daily.weather_code[i]] || WMO_WEATHER[0];
              const dayName = new Date(date + 'T12:00:00').toLocaleDateString(localeStr, { weekday: 'short' });
              return (
                <div
                  key={date}
                  style={{
                    textAlign: 'center', cursor: 'pointer', padding: '4px 6px', borderRadius: 6,
                    background: hoveredDay === i ? 'var(--c-surface-2)' : 'transparent',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={() => setHoveredDay(i)}
                  onMouseLeave={() => setHoveredDay(null)}
                >
                  <div style={{ fontSize: 10, color: 'var(--c-text-muted)', fontWeight: 600, textTransform: 'capitalize', marginBottom: 3 }}>
                    {dayName}
                  </div>
                  <div style={{ fontSize: 16, lineHeight: 1, marginBottom: 3 }}>{dayWmo.emoji}</div>
                  <div style={{ fontSize: 10, fontWeight: 600 }}>{Math.round(daily.temperature_2m_max[i])}°</div>
                  <div style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>{Math.round(daily.temperature_2m_min[i])}°</div>
                </div>
              );
            })}
          </div>
          {hoveredDay !== null && daily?.time?.[hoveredDay] && (() => {
            const hours = getHourlyForDay(daily.time[hoveredDay]);
            if (hours.length === 0) return null;
            const dayName = new Date(daily.time[hoveredDay] + 'T12:00:00').toLocaleDateString(localeStr, { weekday: 'long' });
            return (
              <div style={{
                marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--c-border)'
              }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'capitalize', marginBottom: 8 }}>
                  {dayName}
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between', overflowX: 'auto' }}>
                  {hours.map(h => (
                    <div key={h.hour} style={{ textAlign: 'center', minWidth: 28, flexShrink: 0 }}>
                      <div style={{ fontSize: 9, color: 'var(--c-text-subtle)', marginBottom: 2 }}>{h.hour}</div>
                      <div style={{ fontSize: 14, lineHeight: 1, marginBottom: 2 }}>{h.wmo.emoji}</div>
                      <div style={{ fontSize: 10, fontWeight: 600 }}>{h.temp}°</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const marcaInstancia = getBrand();   // qué instancia es esta (ver utils/brand)
  const { t } = useTranslation();
  const { user, isAdmin, hasModule } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [refreshSpin, setRefreshSpin] = useState(false);

  // Refresh every query that powers a dashboard widget (or the page-level
  // stats fetch). Each widget uses a queryKey that starts with one of these
  // prefixes — invalidateQueries(predicate) refetches them all at once.
  const refreshAll = async () => {
    if (refreshSpin) return;
    setRefreshSpin(true);
    const prefixes = [
      'dashboard-stats', 'dashboard-events', 'dashboard-events-today',
      'dashboard-birthdays', 'dashboard-leaderboard', 'dashboard-ticket-trend',
      'dashboard-top-companies', 'dashboard-ticket-channels',
      'agent-presence', 'billing-queue', 'my-open-tickets',
      'backup-monitor-stats', 'comms-health', 'worker-health-heartbeats',
      'feedback-claude-shipped', 'monitor-alerts-dashboard',
      'time-me-summary', 'tickets-ratings-dashboard',
    ];
    queryClient.invalidateQueries({
      predicate: (q) => prefixes.some(p => q.queryKey?.[0] === p),
    });
    // Tiny visual delay so the spin animation actually shows even on fast nets
    setTimeout(() => setRefreshSpin(false), 600);
  };

  // /dashboard/stats is the shared payload — overview cards, charts, monitor,
  // workshop all read from it via ctx (no per-widget refetch for these).
  // statusWindow defaults to 'active' so the page-level query matches the
  // default-view of the pie-chart widget and they share a cache hit.
  const statusWindow = 'active';

  // Per-user dashboard layout for the customizable widget grid below stats.
  const { layout, save: saveLayout } = useDashboardLayout({ user, isAdmin, hasModule });
  const [editingDash, setEditingDash] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stats', statusWindow],
    queryFn: () => api.get('/dashboard/stats', { params: { status_window: statusWindow } }).then(r => r.data),
    refetchInterval: 60000
  });

  // Presence is also fetched here for the "online now" pill in the header.
  // Widgets that need presence query the same key — React Query dedups it.
  const { data: presenceData } = useQuery({
    queryKey: ['agent-presence'],
    queryFn: () => api.get('/presence/agents').then(r => r.data),
    refetchInterval: 30000,
    enabled: isAdmin
  });

  // WordPress / Web Review sites (company-scoped for agents; admin sees all). Only if module is on.
  const { data: webReviewData } = useQuery({
    queryKey: ['web-review-sites-dash'],
    queryFn: () => api.get('/web-review/sites').then(r => r.data).catch(() => ({ sites: [] })),
    refetchInterval: 300000,
    enabled: hasModule('web_review'),
  });
  const webReviewSites = webReviewData?.sites || [];

  if (isLoading) return (
    <div style={{ padding: 24 }}>
      <SkeletonStatsGrid count={6} />
      <div className="responsive-2col" style={{ marginBottom: 16 }}>
        <SkeletonCard height={260} />
        <SkeletonCard height={260} />
      </div>
      <SkeletonCard height={180} />
    </div>
  );

  const { overview, byStatus = [], byPriority = [], recentTickets = [], ticketTrend = [], monitorSummary, workshopSummary = [], papeleraOverview, papeleraCompanyId } = data || {};
  const agents = presenceData || [];
  const onlineCount = agents.filter(a => a.presence === 'online').length;

  // Widget URLs mirror the backend count semantics (see dashboard.js):
  //   active = status IN ('new','open','pending','on_hold'), Papelera excluded
  const last7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const statCards = [
    {
      label: t('dashboard.openTickets'),
      value: overview?.open_tickets || 0,
      icon: <Ticket size={28} />,
      color: 'var(--c-open)',
      slug: 'open_tickets',
      // The number counts human work only. Spell out what it leaves out so the
      // backlog total never looks like it doesn't add up (see dashboard.js).
      sub: (parseInt(overview?.alerts_active) > 0 || parseInt(overview?.papelera_active) > 0) ? (
        <>
          {parseInt(overview?.alerts_active) > 0 && (
            <span title={t('dashboard.excludedAlertsHint')}>
              +{overview.alerts_active} {t('dashboard.excludedAlerts')}
            </span>
          )}
          {parseInt(overview?.alerts_active) > 0 && parseInt(overview?.papelera_active) > 0 && ' \u00b7 '}
          {parseInt(overview?.papelera_active) > 0 && (
            <span
              title={t('dashboard.excludedPapeleraHint')}
              onClick={(e) => { e.stopPropagation(); navigate(`/tickets?company_id=${papeleraCompanyId}&status=__active__`); }}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              +{overview.papelera_active} {t('dashboard.excludedPapelera')}
            </span>
          )}
        </>
      ) : null,
      onClick: () => navigate('/tickets?status=__active__')
    },
    {
      label: t('dashboard.unassigned'),
      value: overview?.unassigned || 0,
      icon: <UserX size={28} />,
      color: parseInt(overview?.unassigned) > 0 ? '#ef4444' : 'var(--c-text-muted)',
      slug: 'unassigned',
      onClick: () => navigate('/tickets?status=__active__&assigned_to=__unassigned__')
    },
    { label: t('dashboard.resolvedThisWeek'), slug: 'resolved_this_week', value: overview?.resolved_this_week || 0, icon: <CheckCircle2 size={28} />, color: 'var(--c-resolved)', onClick: () => navigate(`/tickets?status=resolved&resolved_after=${encodeURIComponent(last7d)}`) },
    { label: t('dashboard.newToday'), slug: 'new_today', value: overview?.new_today || 0, icon: <TrendingUp size={28} />, color: 'var(--c-brand)', onClick: () => navigate(`/tickets?date_from=${encodeURIComponent(last24h)}`) },
    { label: t('dashboard.urgentOpen'), slug: 'urgent_open', value: overview?.urgent_open || 0, icon: <AlertTriangle size={28} />, color: 'var(--c-urgent)', onClick: () => navigate('/tickets?status=__active__&priority=urgent') },
    {
      label: t('dashboard.avgResponseTime'),
      value: overview?.avg_first_response_hrs
        ? `${parseFloat(overview.avg_first_response_hrs).toFixed(1)}${t('dashboard.hours')}`
        : '—',
      icon: <Clock size={28} />,
      color: 'var(--c-pending)'
    },
  ];

  return (
    <div className="animate-in">
      <AnnouncementModal />
      <HiddenChocolate />
      <VpsDeployBanner />
      <div className="page-header" style={{ flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="page-title">{t('dashboard.dashboard')}</div>
          <div className="page-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>{new Date().toLocaleDateString(user?.language === 'en' ? 'en-US' : 'es-AR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            {!isMobile && <DailyQuote />}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <WelcomeHelpButton label />
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => { track('dashboard', 'refresh'); refreshAll(); }}
          disabled={refreshSpin}
          title={t('dashboard.widgets.refreshAll', 'Refresh all widgets')}
        >
          <RefreshCw
            size={13}
            style={{
              animation: refreshSpin ? 'spin 0.8s linear infinite' : 'none',
            }}
          />
        </button>
        <PresetsMenu onApply={saveLayout} authCtx={{ user, isAdmin, hasModule }} />
        <button
          className={editingDash ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
          onClick={() => { track('dashboard', 'edit_layout_toggle'); setEditingDash(v => !v); }}
          title={t('dashboard.widgets.customize', 'Customize')}
        >
          {editingDash ? <><Check size={13} /> {t('common.done', 'Done')}</> : <><LayoutGrid size={13} /> {t('dashboard.widgets.customize', 'Customize')}</>}
        </button>
        {editingDash && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => { track('dashboard', 'widget_picker_open'); setPickerOpen(true); }}
          >
            {t('dashboard.widgets.addRemove', 'Add / remove')}
          </button>
        )}
        <WeatherWidget language={user?.language} />
        {isAdmin && (
          <div style={{ position: 'relative' }}
            onMouseEnter={e => e.currentTarget.querySelector('.online-tooltip').style.display = 'block'}
            onMouseLeave={e => e.currentTarget.querySelector('.online-tooltip').style.display = 'none'}
          >
            <div
              onClick={() => navigate('/agent-status')}
              style={{ fontSize: 12, color: 'var(--c-text-muted)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, border: '1px solid var(--c-border)', background: 'var(--c-surface-2)' }}
            >
              <Circle size={8} fill="#10b981" color="#10b981" />
              {onlineCount} agent{onlineCount !== 1 ? 's' : ''} online
            </div>
            <div className="online-tooltip" style={{
              display: 'none', position: 'absolute', right: 0, top: 'calc(100% + 8px)',
              background: 'var(--c-surface)', border: '1px solid var(--c-border)',
              borderRadius: 10, padding: '12px 14px', zIndex: 100, minWidth: 200,
              boxShadow: 'var(--shadow-lg)'
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                Online now
              </div>
              {agents.filter(a => a.presence === 'online').length === 0
                ? <div style={{ fontSize: 12, color: 'var(--c-text-muted)', fontStyle: 'italic' }}>No agents online</div>
                : agents.filter(a => a.presence === 'online').map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, fontSize: 13 }}>
                    <Circle size={7} fill="#10b981" color="#10b981" style={{ flexShrink: 0 }} />
                    <span style={{ fontWeight: 500, flex: 1 }}>{a.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{a.open_tickets || 0} open</span>
                  </div>
                ))
              }
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--c-border)', fontSize: 11, color: 'var(--c-brand)', textAlign: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/agent-status')}>
                View all agents →
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Managed Home Assistants (Casas Managed + BrandPlus video wall) —
          admin-only quick access at the very top, per Andy's ask. */}
      {isAdmin && <ManagedHomesStrip />}

      {/* Pick-your-pet reminder — only for internal users who haven't chosen yet.
          Soft inline banner, not a modal. Game stays opt-in but visible. */}
      {user && !user.pokemonChoice && ['admin', 'manager', 'agent'].includes(user.role) && (
        <div
          onClick={() => navigate('/game#pokemon-picker')}
          className="card"
          style={{
            marginBottom: 16,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            borderLeft: '3px solid var(--c-brand)',
          }}
        >
          <Sparkles size={20} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
              {t('dashboard.pickPetBanner.title')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
              {t('dashboard.pickPetBanner.body')}
            </div>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
        </div>
      )}

      {/* Live sessions (admin-only, renders nothing for anyone else) */}
      <LiveSessionsPanel />

      {/* Stat cards */}
      <div className="stats-grid">
        {statCards.map((card) => (
          <div
            className="stat-card"
            key={card.label}
            onClick={card.onClick ? () => { track('dashboard', 'stat_card', card.slug); card.onClick(); } : undefined}
            style={{ cursor: card.onClick ? 'pointer' : 'default' }}
          >
            <div className="stat-label">{card.label}</div>
            <div className="stat-value" style={{ color: card.color }}>{card.value}</div>
            {card.sub && (
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 2, lineHeight: 1.4 }}>
                {card.sub}
              </div>
            )}
            <div className="stat-icon" style={{ color: card.color }}>{card.icon}</div>
          </div>
        ))}
      </div>

      {/* UPS strip — self-populating, company-scoped (Ezequiel/Beto see Papelera, admins see all) */}
      <UpsDashboardStrip />

      {/* Internet links strip — self-populating, company-scoped (same pattern as UPS) */}
      <IspLinksDashboardStrip />

      {/* WordPress / Web Review — sitios administrados (scoped por empresa) */}
      {webReviewSites.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--c-text-muted)', marginBottom: 10 }}>
            <Globe size={15} /> {t('webReview.title')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {webReviewSites.map((s) => {
              const L = s.live;
              const pending = L ? L.plugin_updates + L.theme_updates + (L.core_update ? 1 : 0) : null;
              const ok = L && pending === 0;
              return (
                <div key={s.id} className="card" style={{ margin: 0, cursor: 'pointer' }} onClick={() => navigate('/web-review')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Globe size={16} color="var(--c-brand)" />
                    <span style={{ fontWeight: 600 }}>{s.name}</span>
                    {s.company_name && <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>· {s.company_name}</span>}
                  </div>
                  {L ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: ok ? 'var(--c-resolved)' : 'var(--c-pending)' }}>
                      {ok ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                      {ok ? t('webReview.upToDate') : t('webReview.pendingN', { n: pending })}
                    </div>
                  ) : <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{t('webReview.offline')}</div>}
                  <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 8 }}>
                    {s.last_backup_at ? `${t('webReview.backup')}: ${new Date(s.last_backup_at).toLocaleDateString()}${s.last_restore_ok ? ' ✅' : ''}` : ''}
                    {s.auto_security_updates ? ` · ${t('webReview.autoOn')}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Papelera Berazategui — bloque sólo para admin (Papelera queda fuera de los widgets
          principales). `esAyudaTecno`: es un cliente NUESTRO cableado por id — en el helpdesk
          de otra empresa no tiene ningún sentido, y a Andy le apareció en el de Wolfsohn. */}
      {marcaInstancia.esAyudaTecno && isAdmin && papeleraOverview && papeleraCompanyId && (() => {
        const papCid = encodeURIComponent(papeleraCompanyId);
        const papCards = [
          { label: t('dashboard.openTickets'), value: papeleraOverview.open_tickets || 0, icon: <Ticket size={24} />, color: 'var(--c-open)', onClick: () => navigate(`/tickets?company_id=${papCid}&status=__active__`) },
          { label: t('dashboard.unassigned'), value: papeleraOverview.unassigned || 0, icon: <UserX size={24} />, color: parseInt(papeleraOverview.unassigned) > 0 ? '#ef4444' : 'var(--c-text-muted)', onClick: () => navigate(`/tickets?company_id=${papCid}&status=__active__&assigned_to=__unassigned__`) },
          { label: t('dashboard.resolvedThisWeek'), value: papeleraOverview.resolved_this_week || 0, icon: <CheckCircle2 size={24} />, color: 'var(--c-resolved)', onClick: () => navigate(`/tickets?company_id=${papCid}&status=resolved&resolved_after=${encodeURIComponent(last7d)}`) },
          { label: t('dashboard.newToday'), value: papeleraOverview.new_today || 0, icon: <TrendingUp size={24} />, color: 'var(--c-brand)', onClick: () => navigate(`/tickets?company_id=${papCid}&date_from=${encodeURIComponent(last24h)}`) },
          { label: t('dashboard.urgentOpen'), value: papeleraOverview.urgent_open || 0, icon: <AlertTriangle size={24} />, color: 'var(--c-urgent)', onClick: () => navigate(`/tickets?company_id=${papCid}&status=__active__&priority=urgent`) },
        ];
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('dashboard.papeleraSection')}
              </div>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)', opacity: 0.7 }}>
                {t('dashboard.papeleraSectionHint')}
              </div>
            </div>
            <div className="stats-grid">
              {papCards.map((card) => (
                <div
                  className="stat-card"
                  key={`pap-${card.label}`}
                  onClick={card.onClick}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="stat-label">{card.label}</div>
                  <div className="stat-value" style={{ color: card.color }}>{card.value}</div>
                  <div className="stat-icon" style={{ color: card.color }}>{card.icon}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Quick Links */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'AyudaTecno', url: 'https://www.ayudatecno.com.ar', icon: <Globe size={14} /> },
          { label: 'Instagram', url: 'https://www.instagram.com/ayudatecno', icon: <Instagram size={14} /> },
          { label: 'Anura', url: 'https://panel.anura.com.ar/home', icon: <ExternalLink size={14} /> },
          { label: 'BlueHost', url: 'https://www.bluehost.com/my-account/login', icon: <ExternalLink size={14} /> },
          { label: 'Unifi', url: 'https://unifi.ui.com/', icon: <ExternalLink size={14} /> },
          { label: 'MS Activation', copy: 'irm https://get.activated.win | iex', icon: <Clipboard size={14} /> },
        ].map((link) => (
          <button
            key={link.label}
            onClick={() => {
              if (link.copy) {
                navigator.clipboard.writeText(link.copy);
                toast.success('Comando copiado');
              } else {
                window.open(link.url, '_blank');
              }
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', fontSize: 12, borderRadius: 999,
              border: '1px solid var(--border-color)', background: 'var(--card-bg)',
              color: 'var(--text-primary)', cursor: 'pointer',
            }}
          >
            {link.icon} {link.label}
          </button>
        ))}
      </div>

      {/* Customizable widget grid. ctx forwards the /dashboard/stats payload
          so migrated widgets (charts, monitor, workshop) reuse the same fetch
          instead of firing duplicate requests. */}
      <DashboardGrid
        layout={layout}
        ctx={{ recentTickets, byStatus, byPriority, ticketTrend, monitorSummary, workshopSummary }}
        editing={editingDash}
        onLayoutChange={saveLayout}
      />

      <WidgetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        layout={layout}
        onChange={saveLayout}
        authCtx={{ user, isAdmin, hasModule }}
      />
    </div>
  );
}
